import api from '/src/scripts/api'
import _orderBy from 'lodash/orderBy'
import { parallel } from '../../scripts/parallel'
import { downloadWord } from '../../scripts/downloadWord'

export const teacherHost = process.env.VUE_APP_ENV === 'PUBLISH' || process.env.VUE_APP_ENV === 'PRE'
  ? 'pigai.hexinedu.com'
  : 'zuoye.hexin.im'

export const studentDocTplUrl = 'http://stable-static.hexinedu.com/template/pigai/students.docx'

/**
 * @typedef {{
 *   id: number,
 *   uid: string,
 *   name: string,
 *   grade: string,
 *   group_type: number,
 *   time_create: string,
 *   level: string,
 *   total: number,
 *   bind_total: number,
 * }} Group
 * grade 入学年份
 * level 学段
 * total 学生数量
 * bind_total 绑定学生数量
 */

/**
 * 获取班级列表
 * @returns {Promise<Group[]>}
 */
export async function getGroupList () {
  return api.get('user/teacher/group/list')
}

/**
 * @typedef {{
 *   uid: string,
 *   name: string,
 *   grade: string,
 *   group_type: number,
 *   level: string,
 *   teachers: number,
 *   students: number,
 * }} SchoolGroup
 */
/** 获取学校班级列表
 * @returns {Promise<SchoolGroup[]>}
 */
export async function getSchoolGroupList () {
  return api.get('user/school/group/list')
}

/**
 * @typedef {{
 *   id: number,
 *   uid: string,
 *   short_name: string,
 *   full_name: string,
 *   admin: {name: string},
 *   description: string,
 *   president: string,
 *   zip_code: string,
 *   time_join: string,
 *   level_id: number,
 *   location_id: number,
 *   pay_mode: number,
 *   expire: string,
 *   detail_location: string,
 *   vip_level: number,
 *   funcs: SchoolFuncs,
 * }} SchoolInfo
 * @typedef {{
 *   reading: boolean,
 *   speak: boolean,
 *   student: boolean,
 *   tiku: boolean,
 *   words: boolean,
 *   words_student: boolean,
 *   zuoye: boolean,
 *   zuoye_student: boolean,
 * }} SchoolFuncs
 * @typedef {{
 *   groups: number,
 *   teachers: number,
 *   exams: number,
 *   students: number,
 *   scanners: number,
 *   exercises: number,
 * }} SchoolCounts
 */
/**
 * 获取教师基础信息
 * @returns {Promise<{
 *   school: SchoolInfo,
 *   counts: SchoolCounts,
 *   groups: string[],
 * }>}
 */
export async function getTeacherDetailInfo () {
  return api.get('user/group/list?simple')
}

/**
 * 枚举：学业
 * @type {{international: string, k12: string, research: string}}
 */
export const EAcademic = {
  k12: 'k12',
  research: 'research',
  international: 'international',
}

/**
 * 学段
 * name 名称
 * key 唯一key
 * academic 学业
 * @type {{
 *   name: string,
 *   key: string,
 *   academic: string,
 *   index: number,
 * }[]}
 */
export const Stages = [
  { name: '高中', key: 'senior', academic: EAcademic.k12 },
  { name: '初中', key: 'junior', academic: EAcademic.k12 },
  { name: '小学', key: 'primary', academic: EAcademic.k12 },
  { name: '考研', key: 'research', academic: EAcademic.research },
  { name: 'ALEVEL-CAIE', key: 'ALEVEL-CAIE', academic: EAcademic.international },
  { name: 'ALEVEL-EDX', key: 'ALEVEL-EDX', academic: EAcademic.international },
  { name: 'AP', key: 'AP', academic: EAcademic.international },
  { name: 'IG-CAIE', key: 'IG-CAIE', academic: EAcademic.international },
  { name: 'IG-EDX', key: 'IG-EDX', academic: EAcademic.international }
]

Stages.forEach((s, i) => { s.index = i })

/**
 * 枚举：班级类型
 * @type {{administrative: number, teaching: number}}
 */
export const EGroupType = {
  administrative: 0, // 行政班
  teaching: 1, // 教学班
}

/**
 * 获取学校配置
 * @param params {{school_uid: string}}
 * @returns {Promise<{
 *   id: number,
 *   uid: string,
 *   short_name: string,
 *   full_name: string,
 *   config: {
 *     disable_parent_wechat: boolean,
 *     shut_teacher_create_exercise: boolean,
 *     disable_teacher_create_speak: boolean,
 *     disable_teacher_create_read: boolean,
 *   },
 * }>}
 */
export async function getSchoolOtherConfig (params) {
  return api.get('user/school/other/config', params)
}

/**
 * @typedef {{
 *   relative: number,
 *   time_modify: string,
 *   mobile: string,
 *   unionid: string,
 *   relationship: string,
 *   nickname: string,
 *   openid: string,
 *   avatar: string,
 * }} Wechat
 * @typedef {{
 *   id: number,
 *   uid: string,
 *   name: string,
 *   username: string,
 *   number: string,
 *   wechat: Wechat[],
 * }} Student
 */

/**
 * 获取班级信息（带学生列表）
 * @param params {{group_uid: string}}
 * @returns {Promise<{name: string, grade: string, students: Student[]}>}
 */
export async function getGroupDetail (params) {
  return api.get('user/teacher/group/edit', params)
}

/**
 * @typedef {{
 *  name: string,
 *  role_id: number,
 *  number: string,
 *  }} UserInfo
 */
/**
 * 获取班级老师学生信息
 * @param params {{group_uid: string}}
 * @returns {Promise<{teachers: UserInfo[], students: UserInfo[]}>}
 */
export async function getGroupUserInfo (params) {
  return api.get('user/group/edit', params)
}

/**
 * 修改教师所教班级
 * @param params {{groups: string[]}}
 * @returns {Promise<*>}
 */
export async function modifyTeacherGroup (params) {
  return api.post('user/teacher/group', params)
}

/**
 * 给学生重置密码
 * @param params {{student_uid: string, mobile: string, password: string}}
 * @returns {Promise<*>}
 */
export async function studentResetPassword (params) {
  return api.post('user/passport/student/reset/password', params)
}

/**
 * 解绑学生
 * @param params {{user_uid: string, unionid: string}}
 * @returns {Promise<*>}
 */
export async function studentUnbind (params) {
  return api.post('user/passport/student/unbind', params)
}

/**
 * 导出所有班级学生名单
 * @param groups {Group[]}
 * @param concurrent {number}
 * @returns {Promise<Blob>}
 */
export async function downloadAllGroupStudentsWord (groups, concurrent = 5) {
  const data = await parallel(groups, async group => getGroupDetail({ group_uid: group.uid }), concurrent)
  data.forEach((group, index) => {
    group.total = group.students.length
    group.students = _orderBy(group.students, s => s.number)
    if (index < data.length - 1) {
      group.page_break = true
    }
  })
  return downloadWord(studentDocTplUrl, { groups: data }, '学生名单.docx')
}

/**
 * 导出学生名单
 * @param groupName {string} 班级名称
 * @param students {Student[]} 学生列表
 * @returns {Promise<Blob>}
 */
export async function downloadGroupStudentsWord (groupName, students) {
  const name = `${getGroupName(groupName)}学生名单.docx`
  const group = { students, total: students.length, name: groupName }
  return downloadWord(studentDocTplUrl, { groups: [group] }, name)
}

/**
 * @param params {{group_uid: string}}
 * @returns {Promise<string>}
 */
export async function downloadExcelUrl (params) {
  return api.get('user/student/list/export', params)
}

export function getGroupName (name) {
  if (!name) return name
  return name + (name.indexOf('班') > -1 ? '' : '班')
}

export function maskPhone (input) {
  if (!input || input.length < 11) return input
  return input.substring(0, input.length - 8) + '****' + input.substring(input.length - 4)
}
